import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Image as ImageIcon, Upload, Link, Type, Eye, EyeOff, Save, 
  Loader2, Plus, Trash2, Edit2, X, Monitor, Smartphone,
  AlignLeft, AlignCenter, AlignRight, ExternalLink, Megaphone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BannerAdManagement = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ desktop: false, mobile: false });
  const [showEditor, setShowEditor] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  const defaultAd = {
    enabled: false,
    name: "New Advertisement",
    show_on_vod: true,
    show_on_live: true,
    banner_style: "contained",
    desktop_image_url: null,
    mobile_image_url: null,
    click_url: "",
    alt_text: "Advertisement",
    has_text_overlay: false,
    overlay_headline: "",
    overlay_subtext: "",
    overlay_button_text: "",
    overlay_position: "center",
    show_sponsored_label: true
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const res = await fetch(`${API}/api/banner-ads/admin/all`);
      const data = await res.json();
      setAds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching banner ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAd({ ...defaultAd });
    setShowEditor(true);
  };

  const handleEdit = (ad) => {
    setEditingAd({ ...ad });
    setShowEditor(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);

    try {
      const isNew = !editingAd.id;
      const url = isNew 
        ? `${API}/api/banner-ads/admin/create`
        : `${API}/api/banner-ads/admin/${editingAd.id}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingAd)
      });

      if (res.ok) {
        setSaveMessage({ type: 'success', text: isNew ? 'Advertisement created!' : 'Advertisement updated!' });
        fetchAds();
        setTimeout(() => {
          setShowEditor(false);
          setEditingAd(null);
          setSaveMessage(null);
        }, 1500);
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      console.error('Error saving ad:', err);
      setSaveMessage({ type: 'error', text: 'Failed to save advertisement' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;

    try {
      const res = await fetch(`${API}/api/banner-ads/admin/${adId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchAds();
      }
    } catch (err) {
      console.error('Error deleting ad:', err);
    }
  };

  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [imageType]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);

      const res = await fetch(`${API}/api/banner-ads/admin/upload-image?image_type=${imageType}`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (data.success) {
        const field = imageType === 'desktop' ? 'desktop_image_url' : 'mobile_image_url';
        setEditingAd(prev => ({ ...prev, [field]: data.image_url }));
      }
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-purple-500" />
            Banner Advertisements
          </h2>
          <p className="text-gray-400 mt-1">
            Manage banner ads that appear on VOD and Live homepages
          </p>
        </div>
        
        <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-500">
          <Plus className="h-4 w-4 mr-2" />
          Create Ad
        </Button>
      </div>

      {/* Ads List */}
      {ads.length === 0 ? (
        <div className="bg-white/5 rounded-xl p-12 text-center border border-white/10">
          <ImageIcon className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Advertisements Yet</h3>
          <p className="text-gray-400 mb-4">Create your first banner advertisement to display on VOD and Live pages</p>
          <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-500">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Ad
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {ads.map((ad) => (
            <div key={ad.id} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                {/* Preview Thumbnail */}
                <div className="w-48 h-24 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  {ad.desktop_image_url ? (
                    <img 
                      src={ad.desktop_image_url.startsWith('http') ? ad.desktop_image_url : `${API}${ad.desktop_image_url}`}
                      alt={ad.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-600" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white">{ad.name}</h3>
                    <Badge className={ad.enabled ? 'bg-green-500' : 'bg-gray-500'}>
                      {ad.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                    {ad.show_on_vod && <span className="flex items-center gap-1"><Monitor className="h-3 w-3" /> VOD</span>}
                    {ad.show_on_live && <span className="flex items-center gap-1"><Monitor className="h-3 w-3" /> Live</span>}
                    <span className="capitalize">{ad.banner_style}</span>
                  </div>
                  
                  {ad.click_url && (
                    <p className="text-xs text-gray-500 truncate">
                      <Link className="h-3 w-3 inline mr-1" />
                      {ad.click_url}
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(ad)}
                    className="border-white/20"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(ad.id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && editingAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-gray-900 rounded-2xl border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold">
                {editingAd.id ? 'Edit Advertisement' : 'Create Advertisement'}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-500"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setShowEditor(false); setEditingAd(null); }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Save Message */}
            {saveMessage && (
              <div className={`mx-4 mt-4 p-3 rounded-lg ${
                saveMessage.type === 'success' 
                  ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                  : 'bg-red-500/20 border border-red-500/30 text-red-400'
              }`}>
                {saveMessage.text}
              </div>
            )}

            <div className="p-6 space-y-6">
              {/* Basic Settings */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Advertisement Name</label>
                    <Input
                      value={editingAd.name}
                      onChange={(e) => setEditingAd(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Summer Sale Banner"
                      className="bg-white/10 border-white/20"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="font-medium">Enable Advertisement</p>
                      <p className="text-sm text-gray-400">Show this ad on the selected pages</p>
                    </div>
                    <Switch
                      checked={editingAd.enabled}
                      onCheckedChange={(checked) => setEditingAd(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="block text-sm text-gray-400 mb-2">Display On</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingAd.show_on_vod}
                        onChange={(e) => setEditingAd(prev => ({ ...prev, show_on_vod: e.target.checked }))}
                        className="rounded border-white/20 bg-white/10"
                      />
                      <span>VOD Homepage</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingAd.show_on_live}
                        onChange={(e) => setEditingAd(prev => ({ ...prev, show_on_live: e.target.checked }))}
                        className="rounded border-white/20 bg-white/10"
                      />
                      <span>Live Homepage</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Banner Style</label>
                    <div className="flex gap-2">
                      <Button
                        variant={editingAd.banner_style === 'contained' ? 'default' : 'outline'}
                        onClick={() => setEditingAd(prev => ({ ...prev, banner_style: 'contained' }))}
                        className={editingAd.banner_style === 'contained' ? 'bg-purple-600' : 'border-white/20'}
                      >
                        Contained
                      </Button>
                      <Button
                        variant={editingAd.banner_style === 'full-width' ? 'default' : 'outline'}
                        onClick={() => setEditingAd(prev => ({ ...prev, banner_style: 'full-width' }))}
                        className={editingAd.banner_style === 'full-width' ? 'bg-purple-600' : 'border-white/20'}
                      >
                        Full Width
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Desktop Image */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-blue-400" />
                    Desktop Image
                  </h4>
                  {editingAd.desktop_image_url ? (
                    <div className="relative aspect-[21/9] rounded-lg overflow-hidden mb-3 bg-gray-800">
                      <img 
                        src={editingAd.desktop_image_url.startsWith('http') ? editingAd.desktop_image_url : `${API}${editingAd.desktop_image_url}`}
                        alt="Desktop preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setEditingAd(prev => ({ ...prev, desktop_image_url: null }))}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="aspect-[21/9] rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center mb-3 bg-white/5">
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Recommended: 1920x640</p>
                      </div>
                    </div>
                  )}
                  <label className="block">
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'desktop')} className="hidden" />
                    <Button variant="outline" className="w-full border-white/20" disabled={uploading.desktop} asChild>
                      <span className="cursor-pointer">
                        {uploading.desktop ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {editingAd.desktop_image_url ? 'Change Image' : 'Upload Desktop Image'}
                      </span>
                    </Button>
                  </label>
                </div>

                {/* Mobile Image */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-green-400" />
                    Mobile Image (Optional)
                  </h4>
                  {editingAd.mobile_image_url ? (
                    <div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-3 bg-gray-800 max-w-[200px] mx-auto">
                      <img 
                        src={editingAd.mobile_image_url.startsWith('http') ? editingAd.mobile_image_url : `${API}${editingAd.mobile_image_url}`}
                        alt="Mobile preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setEditingAd(prev => ({ ...prev, mobile_image_url: null }))}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="aspect-[16/9] rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center mb-3 bg-white/5 max-w-[200px] mx-auto">
                      <div className="text-center">
                        <Smartphone className="h-6 w-6 text-gray-500 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">600x400</p>
                      </div>
                    </div>
                  )}
                  <label className="block">
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'mobile')} className="hidden" />
                    <Button variant="outline" className="w-full border-white/20" disabled={uploading.mobile} asChild>
                      <span className="cursor-pointer">
                        {uploading.mobile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {editingAd.mobile_image_url ? 'Change Image' : 'Upload Mobile Image'}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>

              {/* Link & Alt Text */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Click URL</label>
                  <Input
                    value={editingAd.click_url}
                    onChange={(e) => setEditingAd(prev => ({ ...prev, click_url: e.target.value }))}
                    placeholder="https://example.com/landing-page"
                    className="bg-white/10 border-white/20"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Alt Text (Accessibility)</label>
                  <Input
                    value={editingAd.alt_text}
                    onChange={(e) => setEditingAd(prev => ({ ...prev, alt_text: e.target.value }))}
                    placeholder="Description of the advertisement"
                    className="bg-white/10 border-white/20"
                  />
                </div>
              </div>

              {/* Sponsored Label Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div>
                  <p className="font-medium">Show "Sponsored" Label</p>
                  <p className="text-sm text-gray-400">Display a small label above the banner</p>
                </div>
                <Switch
                  checked={editingAd.show_sponsored_label}
                  onCheckedChange={(checked) => setEditingAd(prev => ({ ...prev, show_sponsored_label: checked }))}
                />
              </div>

              {/* Text Overlay Section */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Type className="h-4 w-4 text-yellow-400" />
                    Text Overlay
                  </h4>
                  <Switch
                    checked={editingAd.has_text_overlay}
                    onCheckedChange={(checked) => setEditingAd(prev => ({ ...prev, has_text_overlay: checked }))}
                  />
                </div>

                {editingAd.has_text_overlay && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Headline</label>
                      <Input
                        value={editingAd.overlay_headline || ''}
                        onChange={(e) => setEditingAd(prev => ({ ...prev, overlay_headline: e.target.value }))}
                        placeholder="Big Sale - 50% Off!"
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Subtext</label>
                      <Textarea
                        value={editingAd.overlay_subtext || ''}
                        onChange={(e) => setEditingAd(prev => ({ ...prev, overlay_subtext: e.target.value }))}
                        placeholder="Limited time offer on all products"
                        className="bg-white/10 border-white/20 min-h-[60px]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Button Text</label>
                      <Input
                        value={editingAd.overlay_button_text || ''}
                        onChange={(e) => setEditingAd(prev => ({ ...prev, overlay_button_text: e.target.value }))}
                        placeholder="Shop Now"
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Text Position</label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={editingAd.overlay_position === 'left' ? 'default' : 'outline'}
                          onClick={() => setEditingAd(prev => ({ ...prev, overlay_position: 'left' }))}
                          className={editingAd.overlay_position === 'left' ? 'bg-purple-600' : 'border-white/20'}
                        >
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={editingAd.overlay_position === 'center' ? 'default' : 'outline'}
                          onClick={() => setEditingAd(prev => ({ ...prev, overlay_position: 'center' }))}
                          className={editingAd.overlay_position === 'center' ? 'bg-purple-600' : 'border-white/20'}
                        >
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={editingAd.overlay_position === 'right' ? 'default' : 'outline'}
                          onClick={() => setEditingAd(prev => ({ ...prev, overlay_position: 'right' }))}
                          className={editingAd.overlay_position === 'right' ? 'bg-purple-600' : 'border-white/20'}
                        >
                          <AlignRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerAdManagement;
